import React from 'react'
import { Transaction } from '../../../../types/donation/transaction'
import { useDialogHandler } from '../../../../hooks/dialogs/DialogHandlerHook'
import { TransactionRefundDialog } from '../transactions/dialogs/TransactionRefundDialog'
import { Contract } from '../../../../types/contract'
import { FieldBlock } from '../../fields/FieldBlock'
import { TextField } from '../../fields/TextField'
import { contractStatusStrings, paymentMethodTypeStrings } from '../../../../utils/strings/DonationStrings'
import { ContractStatus } from '../../../../enums/contract-status'
import { translations } from '../../../../utils/translations/translations-manager'
import TableStructure from '../../common/table/TableStructure'
import TransactionsTableBody from './TransactionsTableBody'
import { RecurrenceTranslations } from '@cents/translations-utils'
import { transactionFields } from '../../../../assets/fields/transaction-fields'
import { formatAmount } from '../../../../utils/strings/amount-format'

interface Props {
  subscription: Partial<Contract>
  transactions: Transaction[]
  onTransactionUpdate: () => void
}

const subscriptionFields: Record<string, { getLabel: () => string; getValue: (subscription: Partial<Contract>) => string | undefined }> = {
  status: {
    getLabel: () => translations.texts.status.toString(),
    getValue: subscription => (subscription.status ? contractStatusStrings[subscription.status]() : undefined),
  },
  amount: { getLabel: () => translations.texts.amount.toString(), getValue: subscription => formatAmount((subscription.donationAmount ?? 0) / 100) },
  recurrence: {
    getLabel: () => translations.texts.recurrence.toString(),
    getValue: subscription =>
      subscription.subscriptionRecurrence
        ? RecurrenceTranslations.getRecurrenceString(translations, {
            interval: subscription.subscriptionRecurrence,
            count: subscription.subscriptionRecurrenceCount ?? 1,
          })
        : undefined,
  },
  yearly_amount: {
    getLabel: () => translations.texts.yearly_amount.toString(),
    getValue: subscription => formatAmount(calculateSubscriptionYearAmount(subscription)),
  },
  payment_method: {
    getLabel: () => translations.texts.payment_method.toString(),
    getValue: subscription => (subscription.paymentMethodType ? paymentMethodTypeStrings[subscription.paymentMethodType]() : undefined),
  },
  stipulation_date: {
    getLabel: () => translations.texts.stipulation_date.toString(),
    getValue: subscription => (subscription.createdAt ? new Date(subscription.createdAt).toLocaleString(translations.getLanguage()) : undefined),
  },
  payment_method_last_four: {
    getLabel: () => translations.texts.short_payment_method_last_four.toString(),
    getValue: subscription => subscription.stripePaymentMethodLast4,
  },
}

function calculateSubscriptionYearAmount(contract: Contract): number {
  const amount = (contract.donationAmount ?? 0) / 100
  const count = contract.subscriptionRecurrenceCount ?? 1

  switch (contract.subscriptionRecurrence) {
    case 'day':
      return (amount * 365) / count
    case 'week':
      return (amount * 52) / count
    case 'month':
      return (amount * 12) / count
    case 'year':
    default:
      return amount / count
  }
}

const SubscriptionView: React.FC<Props> = ({ subscription, transactions, onTransactionUpdate }) => {
  const { visible: transactionRefundVisible, close: closeTransactionRefund, open: openTransactionRefund } = useDialogHandler()
  const [selectedTransactionId, setSelectedTransactionId] = React.useState<string>()

  return (
    <div className="subscription-view h-fit flex flex-col gap-3 p-5 bg-cents-white rounded-2xl">
      <div className="subscription-header flex flex-col md:flex-row gap-3 md:items-center">
        <div className="bg-cents-white rounded-2xl flex items-center justify-start gap-3 flex-wrap">
          {subscription && subscription.status && (
            <div
              className={`h-10 w-fit px-8 flex items-center font-bold text-sm rounded-2xl 
              ${
                [ContractStatus.canceled].includes(subscription.status)
                  ? 'bg-red-100 text-red-800'
                  : [ContractStatus.draft, ContractStatus.intent].includes(subscription.status)
                    ? 'bg-yellow-100 text-yallow-800'
                    : [ContractStatus.completed, ContractStatus.finalized].includes(subscription.status)
                      ? 'bg-green-100 text-green-800'
                      : 'text-cents-black bg-cents-grey'
              }`}
            >
              {subscriptionFields['status'].getValue(subscription)}
            </div>
          )}
          <p className="text-cents-purple text-xl lg:text-[22px] font-semibold">{translations.texts.contract_type_recurring.toString()}</p>
        </div>
      </div>
      <div className="subscription-fields h-fit flex flex-wrap gap-3 md:gap-y-5 md:gap-x-10">
        {Object.entries(subscriptionFields).map(([label, value]) => (
          <FieldBlock key={label} label={value.getLabel()} className="whitespace-nowrap md:max-w-[350px]">
            <TextField value={value.getValue(subscription) ?? ''} />
          </FieldBlock>
        ))}
      </div>
      <div className="transactions-container mx-4 flex flex-col mt-2 mb-5 gap-2">
        <span className={`text-sm font-semibold text-cents-black`}>{translations.texts.associated_transactions.toString()}</span>
        <TableStructure tableFields={transactionFields} additionalEntry={<th className="relative h-16 w-[172px] flex-none"></th>} additionalEntryWidth={170}>
          <TransactionsTableBody tableData={transactions} setSelectedTransactionId={setSelectedTransactionId} openTransactionRefund={openTransactionRefund} />
        </TableStructure>
      </div>
      {subscription?.charityId && subscription._id && selectedTransactionId && (
        <TransactionRefundDialog
          charityId={subscription.charityId}
          contractId={subscription._id}
          transactionId={selectedTransactionId}
          visible={transactionRefundVisible}
          close={closeTransactionRefund}
          success={() => {
            onTransactionUpdate()
            closeTransactionRefund()
          }}
        />
      )}
    </div>
  )
}

export default SubscriptionView
